import React, { useMemo, useRef, useState } from "react";

import { useKeyDown } from "@react-hooks-library/core";
import { observer } from "mobx-react-lite";

import { loadVersionOverridingIfNoneMatch } from "src/api";
import {
  CreateMultiversionTestRunVersion,
  MultiversionTestRunResults,
  RouterMultiversionTestRun,
  TestRunVersionProgress,
} from "src/api/endpoints";
import { Flow, FlowVersion } from "src/api/flowTypes";
import { useMultiversionTestRun } from "src/api/queries";
import {
  executionResultsToProgress,
  PLACEHOLDER_PROGRESS,
} from "src/api/testRuns/progress";
import {
  Dataset,
  ErrorBaseInfo,
  FlowRunnerResultV2,
  GraphRunResultType,
  JobRunnerResult,
} from "src/api/types";
import { NodeTypes } from "src/clients/flow-api";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { useCreateDuplicateDatasetJob } from "src/datasets/api/queries";
import { useSelectedDatasetId } from "src/datasets/store/useSelectedDatasetId";
import { useModal } from "src/design-system/Modal";
import {
  RunButton,
  RunButtonDropdownState,
  RunButtonRunState,
} from "src/design-system/RunButton";
import { toastActions } from "src/design-system/Toast/utils";
import { ProgressDropdown } from "src/flowContainer/RunFlowButton/ProgressDropdown";
import { RunBlockedModal } from "src/flowContainer/RunFlowButton/RunBlockedModal";
import { RunTestDropdown } from "src/flowContainer/RunFlowButton/RunTestDropdown";
import { useChildHasAgentNode } from "src/flowContainer/RunFlowButton/useChildHasAgentNodes";
import { useLoadTestRunListener } from "src/flowContainer/RunFlowButton/useLoadTestRunListener";
import { useLoadTestRunResultsListener } from "src/flowContainer/RunFlowButton/useLoadTestRunResultsListener";
import { useTestRunOnJobs } from "src/flowContainer/RunFlowButton/useTestRunOnJobs";
import { hasAgentNodeWithLiveData } from "src/flowContainer/RunFlowButton/utils";
import {
  useStopTestRunListener,
  useTestRunParams,
} from "src/flowContainer/TestRunsSidePane/hooks";
import { useJobRunStatusCountsPolling } from "src/flowGraph/useJobRunCounts";
import { useCanCreateTestRunJob } from "src/hooks/useCanCreateTestRunJob";
import { useIsIcebergLongTestRunsEnabled } from "src/hooks/useIsIcebergLongTestRunsEnabled";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { useTestRunJob } from "src/jobs/api/queries";
import { JobRun } from "src/jobs/types";
import {
  fetchSingleErrorPage,
  invalidateCompletionData,
} from "src/nodeEditor/api/queries";
import { MAX_LTR_ROW_COUNT } from "src/nodeEditor/JobRunIntermediateResultTableWrapper/hooks";
import { useAuthoringContext } from "src/router/routerContextHooks";
import {
  LeftPaneOptions,
  useSetLeftSidePaneOption,
  useToggleLeftSidePaneOption,
} from "src/router/SearchParams";
import { loginWithRefreshToken } from "src/store/api";
import { useNodeHighlighting } from "src/store/NodeHighlighting";
import {
  clearRunStates,
  updateCurrentJobRun,
} from "src/store/runState/RunState";
import { useGraphStore } from "src/store/StoreProvider";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

type Props = {
  flow: Flow;
  version: FlowVersion;
  datasets: Dataset[];
  isLoading: boolean;
};

// Failed test runs carry the root cause in the toast description and there is
// no run history to look it up again, so they stay up far longer than the 4s
// default. The toast keeps its close button, so it can still be dismissed
// right away.
const TEST_RUN_FAILURE_TOAST_DURATION_MS = 30_000;

const onRunError = async (err: unknown) => {
  logger.error(err);
  toastActions.failure({
    title: "Test run failed",
    duration: TEST_RUN_FAILURE_TOAST_DURATION_MS,
    description: errorMessage(err),
    actionText: "Send error report to Taktile",
    onActionClick: () => {
      window.open(
        "mailto:support@taktile.com",
        "_blank",
        "noopener noreferrer",
      );
    },
  });
};
const fillEtagFallbackIfNeeded = (
  versions: FlowVersion[],
): CreateMultiversionTestRunVersion[] =>
  versions.map((version) => ({
    id: version.id,
    etag: version.etag ?? "0000000",
    children: version.child_versions?.length
      ? version.child_versions?.map((child) => ({
          id: child.id,
          etag: child.etag ?? "0000000",
        }))
      : undefined,
  }));

// Wrapper for the additional version dropdowns
// since we need a way to preserve identity of the dropdowns
// for the animations to work
export type AdditionalVersionSelector = {
  selectorId: string;
  version: FlowVersion;
};

const getProgressPercentage = (progress: TestRunVersionProgress[]) => {
  const totalRowsToRun = progress.reduce((acc, curr) => acc + curr.total, 0);

  const processedRows = progress.reduce(
    (acc, curr) =>
      acc + curr.failed + curr.ignored + curr.successful + curr.output_mismatch,
    0,
  );

  return Math.floor((processedRows / totalRowsToRun) * 100);
};

enum TestRunStateType {
  STANDBY = "STANDBY",
  INITIATING = "INITIATING",
  IN_PROGRESS = "IN_PROGRESS",
}

type TestRunState =
  | {
      type: TestRunStateType.STANDBY;
    }
  | {
      type: TestRunStateType.INITIATING;
      versions: string[];
      abortController: AbortController;
      isJobRun: boolean;
    }
  | {
      type: TestRunStateType.IN_PROGRESS;
      versions: { name: string; progress: TestRunVersionProgress }[];
      abortController: AbortController;
      isJobRun: boolean;
    };

const SUPERSEDED_LOAD = "superseded";

export const RunFlowButton: React.FC<Props> = observer(
  ({ flow, version: versionInFocus, datasets, isLoading }) => {
    const { workspace } = useAuthoringContext();
    const { clearTestRunParam } = useTestRunParams();
    const canCreateTestRunJob = useCanCreateTestRunJob();

    const { data: testRunJob } = useTestRunJob({
      baseURL: workspace.base_url!,
      flowId: flow.id,
      flowVersionId: versionInFocus.id,
      enabled: canCreateTestRunJob,
    });

    const isLongTestRunsEnabled = Boolean(
      versionInFocus.meta.enable_long_test_runs,
    );

    const {
      selectedNode,
      nodes,
      nodesArray,
      outputNode,
      setResultsOutdated,
      selectErroredNode,
      selectOutputNode,
      setLastRunDatasetId,
      setDisplayedErroredRow,
      updateResults,
    } = useGraphStore();
    const { clearHighlighting } = useNodeHighlighting();
    const { selectedDatasetId, setSelectedDatasetId } = useSelectedDatasetId();
    const selectedDataset = datasets.find((d) => d.id === selectedDatasetId);
    const hasChildAgentNode = useChildHasAgentNode();
    const hasAgentNodes = useMemo(
      () => hasChildAgentNode || hasAgentNodeWithLiveData(nodesArray),
      [nodesArray, hasChildAgentNode],
    );

    const isIcebergLongTestRunsEnabled = useIsIcebergLongTestRunsEnabled();

    const {
      startTestRunOnJobs,
      stopTestRunOnJobs,
      pollJobRunInProgress,
      setCurrentJobRun,
      currentJobRun,
    } = useTestRunOnJobs();

    useJobRunStatusCountsPolling();

    const {
      isOpen: isRunBlockedModalOpen,
      data: runBlockedModalData,
      openModal: openRunBlockedModal,
      closeModal: closeRunBlockedModal,
    } = useModal<{
      flowVersionName: string;
      datasetName: string;
      datasetSize: number;
      aiNodeNames: string[];
      aiNodeTestRunRowLimit: number;
    }>();

    const { workspace: currentWs, orgId } = useAuthoringContext();
    const aiNodeTestRunRowLimit = Number(
      currentWs.config?.test_run_row_limit ?? 5,
    );

    const { mutateAsync: duplicateDataset, isPending: isDuplicatingDataset } =
      useCreateDuplicateDatasetJob(currentWs.base_url!);

    const toggleSidePaneOptions = useToggleLeftSidePaneOption();
    const setSidePaneOptions = useSetLeftSidePaneOption();
    const toggleDatasetsPane = () =>
      toggleSidePaneOptions(LeftPaneOptions.Datasets);

    const [additionalVersions, setAdditionalVersions] = useState<
      AdditionalVersionSelector[]
    >([]);
    const [testRunState, setTestRunState] = useState<TestRunState>({
      type: TestRunStateType.STANDBY,
    });

    const testRunMutation = useMultiversionTestRun(
      currentWs.base_url!,
      flow.id,
      flow.slug,
    );

    const onDuplicateDataset = async () => {
      if (!selectedDatasetId || !selectedDataset) return;

      try {
        await duplicateDataset({
          flow_id: flow.id,
          request: {
            source_dataset_id: selectedDatasetId,
            new_dataset_name: `${selectedDataset.name} (${aiNodeTestRunRowLimit} rows)`,
            row_limit: aiNodeTestRunRowLimit,
          },
        });
      } catch (err) {
        logger.error("Failed to duplicate dataset", err);
        toastActions.failure({
          title: `Something went wrong when duplicating ${selectedDataset.name}`,
        });
      }

      closeRunBlockedModal();
      toggleDatasetsPane();
    };

    const showNotificationAndTrack = async (
      data: FlowRunnerResultV2 | JobRunnerResult,
      isHistorical: boolean = false,
    ) => {
      if (data.type === GraphRunResultType.SUCCESS) {
        if (
          "nodes" in data &&
          data.nodes.output?.errors_pages_urls?.length > 0 &&
          currentWs.base_url
        ) {
          fetchSingleErrorPage({
            workspaceUrl: currentWs.base_url,
            result: data.nodes.output,
          }).then((errors) => {
            errors.forEach((error) => {
              tracker.emit(
                trackingEvents.authoringTestExecError({
                  flow_id: flow.id,
                  flow_version_id: versionInFocus.id,
                  run_id: testRunMutation.data?.id,
                  error_message: errorMessage(error.example_failure.msg),
                  exception_type: error.example_failure.exc_type,
                  status_code: error.example_failure.status_code,
                  organization_id: orgId,
                  node_id: error.example_failure.node_id,
                  node_type: error.example_failure.node_id
                    ? nodes.get(error.example_failure.node_id)?.type
                    : undefined,
                  type: "row_level",
                  failure_count: error.failure_count,
                }),
              );
            });
          });
        }

        if (!isHistorical) {
          toastActions.success({
            title: "Test run completed",
          });
        }
      } else if (data.type === GraphRunResultType.ERROR) {
        const trackError = (error: ErrorBaseInfo) => {
          tracker.emit(
            trackingEvents.authoringTestExecError({
              flow_id: flow.id,
              flow_version_id: versionInFocus.id,
              run_id: testRunMutation.data?.id,
              error_message: errorMessage(error.msg),
              exception_type: error.exc_type,
              status_code: error.status_code,
              organization_id: orgId,
              type: "flow_level",
              node_id: error.node_id,
              node_type: error.node_id
                ? nodes.get(error.node_id)?.type
                : undefined,
              failure_count: undefined,
            }),
          );
        };
        const numberOfNodesWithCompileError = new Set(
          data.errors?.filter((e) => e.node_id).map((e) => e.node_id),
        ).size;
        if (numberOfNodesWithCompileError <= 1) {
          const error = data.error;
          trackError(error);
          if (error.node_id) {
            const nodeName =
              nodes.get(error.node_id)?.type === NODE_TYPE.INPUT_NODE
                ? "input"
                : "output";

            toastActions.failure({
              title: "Test run failed",
              duration: TEST_RUN_FAILURE_TOAST_DURATION_MS,
              description: (
                <>
                  Check node{" "}
                  <span className="font-inter-semibold-13px text-gray-800">
                    {nodeName}
                  </span>{" "}
                  for details. Autocomplete suggestions will not be updated.
                </>
              ),
            });
          } else {
            toastActions.failure({
              title: "Test run failed",
              duration: TEST_RUN_FAILURE_TOAST_DURATION_MS,
              description: `Error: ${data.error.msg}`,
            });
          }
        } else if (data.errors) {
          data.errors.forEach(trackError);

          toastActions.failure({
            title: "Test run failed",
            duration: TEST_RUN_FAILURE_TOAST_DURATION_MS,
            description: `${numberOfNodesWithCompileError} nodes failed to compile. Autocomplete suggestions will not be updated.`,
          });
        }
      }
    };

    const onTestRunFinished = (
      data: MultiversionTestRunResults,
      isHistorical: boolean = false,
      lastRunDatasetId?: string,
    ) => {
      const versionInFocusResults = data.flow_versions.find(
        (version) => version.id === versionInFocus.id,
      );
      const result = versionInFocusResults?.jobRun
        ? ({
            type: GraphRunResultType.SUCCESS,
            jobRun: versionInFocusResults.jobRun,
            datasetId: data.dataset.id,
          } as const)
        : versionInFocusResults?.execution_results;

      if (result) {
        showNotificationAndTrack(result, isHistorical);
        updateResults(nodesArray, result);
      }

      invalidateCompletionData(versionInFocus.id);
      setLastRunDatasetId(lastRunDatasetId ?? selectedDatasetId ?? null);
      setDisplayedErroredRow(undefined);
      const erroredNode = selectErroredNode();
      if (!erroredNode && !selectedNode) {
        selectOutputNode();
      }
    };

    const filterLiveAiNodes = (version: FlowVersion) => {
      return (
        version.graph?.nodes
          .filter((node) => node?.type === NodeTypes.AI_NODE_V2)
          .filter(
            (node) =>
              node?.meta?.config?.environments_config
                ?.authoring_mode_data_source !== "mock_data",
          ) ?? []
      );
    };

    const isVersionInFocusFinished = (response: MultiversionTestRunResults) => {
      const versionInFocusResult = response.flow_versions.find(
        (version) => version.id === versionInFocus.id,
      );

      return !!versionInFocusResult?.execution_results;
    };

    const hasVersionInFocus = (response: MultiversionTestRunResults) =>
      response.flow_versions.some(
        (version) => version.id === versionInFocus.id,
      );

    const pollTestRunResults = async (
      runId: string,
      abortController: AbortController,
      testRunVersions: FlowVersion[],
    ): Promise<MultiversionTestRunResults | undefined> => {
      let result = await RouterMultiversionTestRun.get(
        currentWs.base_url!,
        runId,
      );

      if (abortController.signal.aborted) {
        return undefined;
      }
      while (
        result.status === 200 &&
        hasVersionInFocus(result.data) &&
        !isVersionInFocusFinished(result.data)
      ) {
        await new Promise((r) => setTimeout(r, 1000));
        result = await RouterMultiversionTestRun.get(
          currentWs.base_url!,
          runId,
        );
        const theresProgressAvailable = result.data.flow_versions.some(
          (version) => version.progress,
        );
        if (abortController.signal.aborted) {
          return undefined;
        }
        if (theresProgressAvailable) {
          setTestRunState({
            isJobRun: false,
            type: TestRunStateType.IN_PROGRESS,
            versions: result.data.flow_versions.map((version) => ({
              name:
                testRunVersions.find((v) => version.id === v.id)?.name ?? "",
              // If the progress is available we use it, otherwise we check
              // if the version has finished already (execution results would be populated)
              // if not we use a placeholder progress where everything is 0
              progress:
                version.progress ??
                (version.execution_results
                  ? executionResultsToProgress(version.execution_results)
                  : null) ??
                PLACEHOLDER_PROGRESS,
            })),
            abortController,
          });
        }
      }

      if (result.status !== 200) {
        throw new Error(
          `Error fetching results url, status: ${result.status} ${result.statusText}`,
        );
      }

      return result.data;
    };

    const handleTestRun = async () => {
      // This abort controller is used in case
      // the test run is stopped, we check in multiple
      // points whether its been aborted
      const abortController = new AbortController();
      abortController.signal.addEventListener("abort", () => {
        setTestRunState({ type: TestRunStateType.STANDBY });
        toastActions.default({ title: "Test run stopped" });
      });

      const testRunVersions = [
        versionInFocus,
        ...additionalVersions.map((v) => v.version),
      ];
      setTestRunState({
        type: TestRunStateType.INITIATING,
        versions: testRunVersions.map((v) => v.name),
        abortController,
        isJobRun: false,
      });
      try {
        if (!selectedDataset) throw Error("no selectedDataset for run");
        clearHighlighting();
        clearTestRunParam();

        // Make sure to get the latest etags
        const testRunVersionIds = testRunVersions.map((v) => v.id);
        const versions = await Promise.all(
          testRunVersionIds.map(loadVersionOverridingIfNoneMatch),
        );

        // For datasets with >10 rows we block the test run if
        // there is a live AI Node in any of the selected versions
        if (
          selectedDataset.row_count &&
          selectedDataset.row_count > aiNodeTestRunRowLimit
        ) {
          for (const version of versions) {
            const liveAiNodes = filterLiveAiNodes(version);

            if (liveAiNodes.length > 0) {
              // Abort the test run and open the modal
              setTestRunState({ type: TestRunStateType.STANDBY });
              openRunBlockedModal({
                flowVersionName: version.name,
                aiNodeNames: liveAiNodes.map((node) => node.name!),
                datasetName: selectedDataset.name,
                datasetSize: selectedDataset.row_count!,
                aiNodeTestRunRowLimit: aiNodeTestRunRowLimit,
              });
              return;
            }
          }
        }

        await loginWithRefreshToken();

        if (abortController.signal.aborted) {
          return;
        }

        const mutationResult = await testRunMutation.mutateAsync({
          dataset_id: selectedDataset.id,
          flow_versions: fillEtagFallbackIfNeeded(versions),
        });
        tracker.emit(
          trackingEvents.clickTestRun({
            organization_id: orgId,
            flow_id: flow.id,
            flow_version_id: versionInFocus.id,
            sample_data_id: selectedDataset.id,
            run_id: mutationResult.id,
          }),
        );

        if (abortController.signal.aborted) {
          return;
        }

        if (isVersionInFocusFinished(mutationResult)) {
          onTestRunFinished(mutationResult);
          return;
        }

        const results = await pollTestRunResults(
          mutationResult.id,
          abortController,
          testRunVersions,
        );

        if (results && !abortController.signal.aborted) {
          onTestRunFinished(results);
        }
      } catch (e) {
        if (!abortController.signal.aborted) {
          onRunError(e);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setTestRunState({ type: TestRunStateType.STANDBY });
        }
      }
    };

    const resultsLoadAbortRef = useRef<AbortController | null>(null);

    const loadTestRunResults = async (runId: string) => {
      const loadingToastId = `test-run-loading-${runId}`;
      resultsLoadAbortRef.current?.abort(SUPERSEDED_LOAD);
      const abortController = new AbortController();
      resultsLoadAbortRef.current = abortController;
      abortController.signal.addEventListener("abort", () => {
        toastActions.dismiss(loadingToastId);
        if (abortController.signal.reason === SUPERSEDED_LOAD) {
          return;
        }
        setTestRunState({ type: TestRunStateType.STANDBY });
        toastActions.default({ title: "Test run stopped" });
      });

      toastActions.loading({
        title: "Loading test run",
        id: loadingToastId,
      });

      clearRunStates(nodesArray, versionInFocus.id);
      clearHighlighting();
      setTestRunState({
        type: TestRunStateType.INITIATING,
        versions: [versionInFocus.name],
        abortController,
        isJobRun: false,
      });

      try {
        const results = await pollTestRunResults(runId, abortController, [
          versionInFocus,
        ]);

        if (!results) {
          return;
        }

        toastActions.dismiss(loadingToastId);

        if (!hasVersionInFocus(results)) {
          toastActions.failure({
            title: "Test run not loaded",
            description:
              "This test run has no results for the version in focus",
          });
          return;
        }

        onTestRunFinished(results, true, results.dataset.id);
      } catch (e) {
        if (!abortController.signal.aborted) {
          toastActions.dismiss(loadingToastId);
          onRunError(e);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setTestRunState({ type: TestRunStateType.STANDBY });
        }
      }
    };

    const cleanupTestRunState = () => {
      clearRunStates(nodesArray, versionInFocus.id);
      clearHighlighting();
      clearTestRunParam();
      setResultsOutdated(true);
    };

    const getJobRunAbortController = () => {
      const abortController = new AbortController();
      abortController.signal.addEventListener("abort", async (event) => {
        const target = event.target as AbortSignal;

        // if stop test run event is from sidepane, we don't want to stop the job run,
        // as it will be handled by the sidepane
        if (target && target.reason === "sidepane") {
          setTestRunState({ type: TestRunStateType.STANDBY });
        } else {
          await stopTestRunOnJobs();
          setTestRunState({ type: TestRunStateType.STANDBY });
          toastActions.default({ title: "Test run stopped" });
        }

        // clean up run state and highlighting
        cleanupTestRunState();
      });

      return abortController;
    };

    // handle job run
    const handleJobRun = async () => {
      if (!selectedDatasetId) {
        return;
      }

      const abortController = getJobRunAbortController();

      try {
        clearHighlighting();
        setDropdownState("closed");

        setTestRunState({
          type: TestRunStateType.INITIATING,
          versions: [versionInFocus.name],
          abortController,
          isJobRun: true,
        });

        const jobRun = await startTestRunOnJobs({
          datasetId: selectedDatasetId,
          datasetName: selectedDataset?.name ?? "",
        });

        if (abortController.signal.aborted) {
          setTestRunState({ type: TestRunStateType.STANDBY });
          return;
        }

        loadJobRun({
          jobRun,
          abortController,
          datasetId: selectedDatasetId,
          datasetEtag: selectedDataset?.etag ?? "",
        });
      } catch (error) {
        if (!abortController.signal.aborted) {
          onRunError(error);
          setTestRunState({ type: TestRunStateType.STANDBY });
          if (currentJobRun.current?.runId) {
            toastActions.dismiss(
              `test-run-loading-${currentJobRun.current.runId}`,
            );
          }
        }
      }
    };

    const loadJobRun = async ({
      jobRun,
      abortController: abortControllerArg,
      datasetId,
      datasetEtag,
      isHistorical = false,
    }: {
      jobRun: JobRun;
      abortController?: AbortController;
      datasetId: string;
      datasetEtag: string;
      isHistorical?: boolean;
    }) => {
      const abortController = abortControllerArg ?? getJobRunAbortController();
      const datasetRowCount =
        datasets.find((d) => d.id === datasetId)?.row_count ?? 1;

      try {
        cleanupTestRunState();
        setCurrentJobRun(jobRun);
        setLastRunDatasetId(datasetId);
        updateCurrentJobRun(jobRun);

        // Set run state immediately so the results table renders during polling
        updateResults(nodesArray, {
          type: GraphRunResultType.JOB_IN_PROGRESS,
          jobRun,
          datasetId,
        });

        if (!isHistorical) {
          setTestRunState({
            isJobRun: true,
            type: TestRunStateType.IN_PROGRESS,
            versions: [
              { name: versionInFocus.name, progress: PLACEHOLDER_PROGRESS },
            ],
            abortController,
          });
        }

        let hasStateFromBackend = false;
        const result = await pollJobRunInProgress({
          expectedTotal: datasetRowCount,
          outputNodeId: outputNode?.id ?? "",
          jobRun,
          callback: (progress) => {
            if (abortController.signal.aborted) {
              return;
            }

            if (isHistorical && !hasStateFromBackend) {
              toastActions.dismiss(`test-run-loading-${jobRun.id}`);
              toastActions.success({
                title: "Test run loaded",
              });
            }

            hasStateFromBackend = true;
            updateResults(nodesArray, {
              type: GraphRunResultType.JOB_IN_PROGRESS,
              jobRun: {
                ...jobRun,
                updated_at: new Date().toISOString(),
                success_rows_count: progress.successful,
                failed_rows_count: progress.failed,
                ignored_rows_count: progress.ignored,
                total_rows_count: progress.total,
              },
              datasetId,
            });
            if (!isHistorical) {
              setTestRunState({
                type: TestRunStateType.IN_PROGRESS,
                versions: [{ name: versionInFocus.name, progress }],
                abortController,
                isJobRun: true,
              });
            }
          },
        });

        if (!abortController.signal.aborted && result?.counts) {
          const progress = {
            total: datasetRowCount,
            successful: result.counts.totalSuccess,
            failed: result.counts.totalFailed,
            ignored: result.counts.totalIgnored,
            output_mismatch: result.counts.totalOutputMismatch,
          } as const;

          onTestRunFinished(
            {
              id: jobRun.id,
              infer_types: false,
              created_by: "",
              created_at: jobRun.created_at,
              dataset: {
                id: datasetId,
                etag: datasetEtag,
                shard_count: 0,
              },
              flow_versions: [
                {
                  id: versionInFocus.id,
                  jobRun: result.jobRun,
                  execution_results: null,
                  etag: versionInFocus.etag ?? "",
                  progress,
                },
              ],
            },
            isHistorical,
          );

          setDropdownState("closed");
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          onRunError(error);
          if (currentJobRun.current?.runId) {
            toastActions.dismiss(
              `test-run-loading-${currentJobRun.current.runId}`,
            );
          }
        }
      } finally {
        if (!abortController.signal.aborted) {
          setTestRunState({ type: TestRunStateType.STANDBY });
        }
      }
    };

    useStopTestRunListener((event) => {
      if (
        event.detail.source === "sidepane" &&
        testRunState.type !== "STANDBY"
      ) {
        testRunState.abortController.abort(event.detail.source);
      }
    });

    useLoadTestRunResultsListener((runId) => {
      void loadTestRunResults(runId);
    }, nodesArray.length > 0);

    useLoadTestRunListener(({ jobRun, datasetId, datasetEtag }) => {
      if (
        currentJobRun.current?.jobId === jobRun.job_id &&
        currentJobRun.current?.runId === jobRun.id
      ) {
        return;
      }

      toastActions.loading({
        title: "Loading test run",
        id: `test-run-loading-${jobRun.id}`,
      });
      loadJobRun({
        jobRun,
        datasetId,
        datasetEtag,
        isHistorical: true,
      });
    });

    const [dropdownState, setDropdownState] =
      useState<RunButtonDropdownState>("closed");

    const runButtonState: RunButtonRunState =
      testRunState.type === "STANDBY"
        ? { type: "standby" }
        : {
            type: "running",
            progress:
              testRunState.type === "IN_PROGRESS"
                ? getProgressPercentage(
                    testRunState.versions.map((v) => v.progress),
                  )
                : null,
          };

    const handleRun = () => {
      const rowCount = selectedDataset?.row_count ?? 0;

      if (
        isIcebergLongTestRunsEnabled &&
        canCreateTestRunJob &&
        (isLongTestRunsEnabled || hasAgentNodes)
      ) {
        if (rowCount > MAX_LTR_ROW_COUNT) {
          toastActions.failure({
            title: `Agent test runs are not supported for datasets with more than ${MAX_LTR_ROW_COUNT} rows`,
          });
        } else {
          handleJobRun();
        }
      } else {
        handleTestRun();
      }
    };

    const handleStop = () => {
      if (testRunState.type !== "STANDBY") {
        testRunState.abortController.abort();
      }
    };

    const canRun = Boolean(selectedDatasetId) && !isLoading;

    /**
     * CMD/CTRL + Enter starts a run, the same as clicking the button.
     * We disable it if a modal (which would intercept a click too) is open.
     */
    useKeyDown(["Enter"], (e) => {
      const modalIsOpen = document.querySelector('[data-loc="modal"]');
      if ((e.metaKey || e.ctrlKey) && !modalIsOpen) {
        e.preventDefault();
        if (testRunState.type === "STANDBY" && canRun) {
          handleRun();
        }
      }
    });

    return (
      <>
        <RunButton
          dataLoc="run-flow-button"
          dropdown={{
            state: dropdownState,
            onStateChange: setDropdownState,
            // disable dropdown if job run is in progress
            disabled: testRunState.type !== "STANDBY" && testRunState.isJobRun,
            children:
              testRunState.type === "STANDBY" ? (
                // With job access, wait for the job so the long-test-runs form
                // initializes from its loaded configuration.
                (!canCreateTestRunJob || testRunJob) && (
                  <RunTestDropdown
                    key={versionInFocus.id}
                    additionalVersions={additionalVersions}
                    canCreateTestRunJob={canCreateTestRunJob}
                    datasets={datasets}
                    flow={flow}
                    hasAgentNodes={hasAgentNodes}
                    isLoading={isLoading}
                    selectedDatasetId={selectedDatasetId}
                    setAdditionalVersions={setAdditionalVersions}
                    testRunJob={testRunJob}
                    version={versionInFocus}
                    onClose={() => setDropdownState("closed")}
                    onGoToSimulations={() =>
                      setSidePaneOptions(LeftPaneOptions.Simulations)
                    }
                    onSelectData={setSelectedDatasetId}
                  />
                )
              ) : (
                <ProgressDropdown
                  showOutputMismatch={true}
                  versions={
                    testRunState.type === "IN_PROGRESS"
                      ? testRunState.versions
                      : testRunState.versions.map((versionName) => ({
                          name: versionName,
                          progress: PLACEHOLDER_PROGRESS,
                        }))
                  }
                />
              ),
          }}
          runDisabled={!canRun}
          state={runButtonState}
          onRun={handleRun}
          onStop={handleStop}
        />
        <RunBlockedModal
          flow={flow}
          isLoading={isDuplicatingDataset}
          isOpen={isRunBlockedModalOpen}
          onClose={closeRunBlockedModal}
          onDuplicateDataset={onDuplicateDataset}
          {...runBlockedModalData}
        />
      </>
    );
  },
);
